import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/
import { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, Box, TextField, DialogActions, IconButton, Tooltip, LinearProgress } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
const TOKEN_DESCRIPTION_SIZE = 255;
export const GenerateTokenDialog = props => {
    const [loading, setLoading] = useState(false);
    const [description, setDescription] = useState('');
    const [descriptionError, setDescriptionError] = useState();
    const [tokenValue, setTokenValue] = useState();
    const [copied, setCopied] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const { open, onClose, title = 'Generate new token', canGenerate: inputValid = true } = props;
    const resetState = () => {
        setLoading(false);
        setDescription('');
        setDescriptionError(undefined);
        setTokenValue(undefined);
        setCopied(false);
        setShowToken(false);
    };
    const handleClose = () => {
        if (!loading) {
            onClose();
            resetState();
        }
    };
    const handleDescriptionChange = (event) => {
        const value = event.target.value;
        setDescription(value);
        setDescriptionError(value.length > TOKEN_DESCRIPTION_SIZE
            ? `Description must not exceed ${TOKEN_DESCRIPTION_SIZE} characters.`
            : undefined);
    };
    const handleGenerate = async () => {
        setLoading(true);
        try {
            const value = await props.onGenerate(description);
            setTokenValue(value);
        }
        catch (err) {
            props.onError?.(err);
        }
        finally {
            setLoading(false);
        }
    };
    const handleCopy = () => {
        if (tokenValue) {
            navigator.clipboard.writeText(tokenValue).then(() => setCopied(true));
            setTimeout(() => setCopied(false), 2000);
        }
    };
    const canGenerate = !descriptionError && !loading && inputValid;
    return (_jsx(_Fragment, { children: open && (_jsxs(Dialog, { open: open, onClose: handleClose, children: [_jsx(DialogTitle, { children: title }), loading && _jsx(LinearProgress, { color: 'secondary' }), _jsx(DialogContent, { children: tokenValue ? (_jsxs(_Fragment, { children: [_jsx(DialogContentText, { sx: { mb: 2 }, children: "Copy this token now. It will not be shown again." }), _jsx(OutlinedInput, { fullWidth: true, readOnly: true, type: showToken ? 'text' : 'password', value: tokenValue, sx: { fontFamily: 'monospace', fontSize: '0.85rem' }, endAdornment: _jsxs(InputAdornment, { position: 'end', children: [_jsx(Tooltip, { title: showToken ? 'Hide' : 'Show', placement: 'top', children: _jsx(IconButton, { size: 'small', onClick: () => setShowToken(v => !v), children: showToken ? (_jsx(VisibilityOffIcon, { fontSize: 'small' })) : (_jsx(VisibilityIcon, { fontSize: 'small' })) }) }), _jsx(Tooltip, { title: copied ? 'Copied!' : 'Copy', placement: 'top', children: _jsx(IconButton, { size: 'small', onClick: handleCopy, children: _jsx(ContentCopyIcon, { fontSize: 'small' }) }) })] }) })] })) : (_jsxs(_Fragment, { children: [_jsx(DialogContentText, { children: "You can add an optional description to help identify where this token is used." }), _jsx(Box, { mt: 2, children: _jsx(TextField, { autoFocus: true, fullWidth: true, label: 'Description (optional)', error: Boolean(descriptionError), helperText: descriptionError, value: description, onChange: handleDescriptionChange, onKeyDown: e => {
                                        if (e.key === 'Enter' && canGenerate) {
                                            handleGenerate();
                                        }
                                    } }) }), props.children] })) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleClose, children: tokenValue ? 'Close' : 'Cancel' }), !tokenValue && (_jsx(Button, { variant: 'contained', color: 'secondary', disabled: !canGenerate, onClick: handleGenerate, children: "Generate Token" }))] })] })) }));
};
//# sourceMappingURL=generate-token-dialog.js.map